import React, { useState, useEffect } from 'react';
import { Card, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader1 from '../../components/common/PageHeader1';
import apiClient from "../../utils/api";

function ReceiptSettingsAdd() {
    const navigate = useNavigate();
    const [exitLocations, setExitLocations] = useState([]);
    const [formData, setFormData] = useState({
        company_logo: false,
        plate_number: false,
        parking_datetime: false,
        ticket_id: false,
        inquiry_number: false,
        terms_conditions: false,
        terms_and_conditions_text: '',
        company_email: false,
        company_email_text: '',
        custom_text: '',
        logo: null,
        exit_location_id: ''
    });

    useEffect(() => {
        fetchExitLocations();
    }, []);

    const fetchExitLocations = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get('accounts/exit-locations', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setExitLocations(response.data.data);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load Valet Locations');
        }
    };

    const handleInputChange = (e) => {
        const { name, type, checked, value, files } = e.target;
        if (type === 'file') {
            setFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('auth_token');
            const data = new FormData();

            // Add all form fields to FormData, converting boolean values correctly
            Object.keys(formData).forEach(key => {
                if (key === 'logo' && formData[key]) {
                    data.append('logo', formData[key]);
                } else if (key !== 'logo') {
                    // Convert boolean values to 0 or 1 for these specific fields
                    const booleanFields = [
                        'company_logo',
                        'plate_number',
                        'parking_datetime',
                        'ticket_id',
                        'inquiry_number',
                        'terms_conditions',
                        'company_email'
                    ];
                    
                    if (booleanFields.includes(key)) {
                        data.append(key, formData[key] ? 1 : 0);
                    } else {
                        data.append(key, formData[key]);
                    }
                }
            });

            const response = await apiClient.post('accounts/receipt-settings', data, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            toast.success('Receipt setting created successfully');
            navigate('/accounts/receipt-settings-list');
        } catch (error) {
            console.error('Error:', error);
            if (error.response?.data?.errors) {
                Object.values(error.response.data.errors).forEach(err => {
                    toast.error(err[0]);
                });
            } else {
                toast.error('Error creating receipt setting');
            }
        }
    };

    return (
        <div className="body d-flex py-3">
            <div className="container-xxl">
                <PageHeader1 pagetitle='Add Receipt Setting' />
                <Card>
                    <Card.Body>
                        <Form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <Form.Check
                                        type="switch"
                                        id="company_logo"
                                        name="company_logo"
                                        label="Company Logo"
                                        checked={formData.company_logo}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Form.Check
                                        type="switch"
                                        id="plate_number"
                                        name="plate_number"
                                        label="Plate Number"
                                        checked={formData.plate_number}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Form.Check
                                        type="switch"
                                        id="parking_datetime"
                                        name="parking_datetime"
                                        label="Date & Time of Parking"
                                        checked={formData.parking_datetime}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Form.Check
                                        type="switch"
                                        id="ticket_id"
                                        name="ticket_id"
                                        label="Ticket ID"
                                        checked={formData.ticket_id}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Form.Check
                                        type="switch"
                                        id="inquiry_number"
                                        name="inquiry_number"
                                        label="Inquiry Number"
                                        checked={formData.inquiry_number}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Form.Check
                                        type="switch"
                                        id="terms_conditions"
                                        name="terms_conditions"
                                        label="Terms & Conditions"
                                        checked={formData.terms_conditions}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Form.Check
                                        type="switch"
                                        id="company_email"
                                        name="company_email"
                                        label="Company Email"
                                        checked={formData.company_email}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                {formData.company_logo && (
                                    <div className="col-12">
                                        <Form.Group>
                                            <Form.Label>Upload Logo</Form.Label>
                                            <Form.Control
                                                type="file"
                                                name="logo"
                                                onChange={handleInputChange}
                                                accept="image/*"
                                            />
                                            <Form.Text className="text-muted">
                                                Supported formats: JPEG, PNG, JPG, GIF (max 2MB)
                                            </Form.Text>
                                        </Form.Group>
                                    </div>
                                )}

                                {formData.terms_conditions && (
                                    <div className="col-12">
                                        <Form.Group>
                                            <Form.Label>Terms & Conditions Text</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                name="terms_and_conditions_text"
                                                value={formData.terms_and_conditions_text}
                                                onChange={handleInputChange}
                                                placeholder="Enter terms and conditions..."
                                            />
                                        </Form.Group>
                                    </div>
                                )}

                                {formData.company_email && (
                                    <div className="col-12">
                                        <Form.Group>
                                            <Form.Label>Company Email Text</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="company_email_text"
                                                value={formData.company_email_text}
                                                onChange={handleInputChange}
                                                placeholder="Enter company email..."
                                            />
                                        </Form.Group>
                                    </div>
                                )}

                                <div className="col-12">
                                    <Form.Group>
                                        <Form.Label>Custom Text</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            name="custom_text"
                                            value={formData.custom_text}
                                            onChange={handleInputChange}
                                            placeholder="Enter custom text for receipt..."
                                        />
                                    </Form.Group>
                                </div>

                                <div className="col-md-12">
                                    <Form.Group>
                                        <Form.Label>Valet Location</Form.Label>
                                        <Form.Select
                                            name="exit_location_id"
                                            value={formData.exit_location_id}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Select Valet Location</option>
                                            {exitLocations.map(location => (
                                                <option key={location.id} value={location.id}>
                                                    {location.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>

                                <div className="col-12">
                                    <Button type="submit" variant="primary">
                                        Save Setting
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
}

export default ReceiptSettingsAdd;